import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../store/hooks';
import { classNames } from '../../utils/compatibility';

interface SyncStatusProps {
  className?: string;
  showText?: boolean;
  iconOnly?: boolean;
  showCount?: boolean;
  size?: 'sm' | 'md' | 'lg';
}

/**
 * Component to display the synchronization status
 */
const SyncStatus: React.FC<SyncStatusProps> = ({
  className = '',
  showText = true,
  iconOnly = false,
  showCount = false,
  size = 'md'
}) => {
  const { t } = useTranslation();
  const { isOnline, pendingChanges } = useAppSelector(state => state.training);
  
  const pendingCount = pendingChanges.length;
  const hasPending = pendingCount > 0;
  
  // Calculate size-dependent classes
  const getSizeClasses = (size: 'sm' | 'md' | 'lg') => {
    switch (size) {
      case 'sm':
        return {
          container: 'px-1.5 py-0.5 text-xs',
          icon: 'h-1.5 w-1.5 mr-1'
        };
      case 'lg':
        return {
          container: 'px-3 py-1.5 text-sm',
          icon: 'h-3 w-3 mr-1.5'
        };
      default: // 'md'
        return {
          container: 'px-2 py-1 text-xs',
          icon: 'h-2 w-2 mr-1'
        };
    }
  };
  
  const sizeClasses = getSizeClasses(size);
  
  if (!isOnline) {
    return (
      <span className={classNames(
        'inline-flex items-center rounded-full bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200',
        iconOnly ? 'p-1' : sizeClasses.container,
        className
      )}>
        <svg 
          className={classNames(
            'animate-pulse fill-red-500',
            iconOnly ? 'h-2 w-2' : sizeClasses.icon
          )} 
          viewBox="0 0 6 6" 
          aria-hidden="true"
        >
          <circle cx="3" cy="3" r="3" />
        </svg>
        {!iconOnly && showText && t('sync.status.offline')}
      </span>
    );
  }
  
  if (hasPending) {
    return (
      <span className={classNames(
        'inline-flex items-center rounded-full bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-200',
        iconOnly ? 'p-1' : sizeClasses.container,
        className
      )}>
        <svg 
          className={classNames(
            'animate-pulse fill-amber-500',
            iconOnly ? 'h-2 w-2' : sizeClasses.icon
          )} 
          viewBox="0 0 6 6" 
          aria-hidden="true"
        >
          <circle cx="3" cy="3" r="3" />
        </svg>
        {!iconOnly && showText && t('sync.status.syncing')}
        {!iconOnly && showCount && pendingCount > 0 && (
          <span className="ml-1 rounded-full bg-amber-200 px-1.5 py-0.5 text-xs font-semibold text-amber-800 dark:bg-amber-800 dark:text-amber-200">
            {pendingCount}
          </span>
        )}
      </span>
    );
  }
  
  return (
    <span className={classNames(
      'inline-flex items-center rounded-full bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-200',
      iconOnly ? 'p-1' : sizeClasses.container,
      className
    )}>
      <svg 
        className={classNames(
          'fill-green-500',
          iconOnly ? 'h-2 w-2' : sizeClasses.icon
        )} 
        viewBox="0 0 6 6" 
        aria-hidden="true"
      >
        <circle cx="3" cy="3" r="3" />
      </svg>
      {!iconOnly && showText && t('sync.status.synced')}
    </span>
  );
};

export default SyncStatus;